import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface PrivacyPolicyProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PrivacyPolicy = ({ open, onOpenChange }: PrivacyPolicyProps) => {
  const { data: privacy, isLoading } = useQuery({
    queryKey: ["privacy-policy"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legal_documents")
        .select("content")
        .eq("type", "privacy_policy")
        .single();

      if (error) throw error;
      return data.content;
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Privacy Policy</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[calc(80vh-100px)] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-20">
              <p>Loading privacy policy...</p>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <div className="whitespace-pre-wrap text-sm space-y-4">
                {privacy?.split('\n').map((line, index) => {
                  const trimmedLine = line.trim();
                  // Check if line is a main section header (starts with a number followed by dot)
                  if (/^\d+\.(?!\d)/.test(trimmedLine)) {
                    return <h2 key={index} className="text-lg font-bold mt-6 mb-3">{trimmedLine}</h2>;
                  }
                  // Check if line is a subsection header (starts with number.number)
                  if (/^\d+\.\d+/.test(trimmedLine)) {
                    return <h3 key={index} className="text-md font-semibold mt-4 mb-2">{trimmedLine}</h3>;
                  }
                  // Check if line is a bullet point
                  if (trimmedLine.startsWith('•')) {
                    return (
                      <div key={index} className="flex items-start space-x-2 ml-4">
                        <span>•</span>
                        <span>{trimmedLine.substring(1).trim()}</span>
                      </div>
                    );
                  }
                  // Regular paragraph
                  return trimmedLine ? (
                    <p key={index} className="my-2">{trimmedLine}</p>
                  ) : (
                    <div key={index} className="h-4" /> // Empty line spacing
                  );
                })}
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};